/**
 * Modal de Gerenciamento de Solicitações de Unpicking (Fase 4)
 *
 * Lista todas as solicitações de unpicking de uma saída.
 * Usuários autorizados podem aprovar ou rejeitar solicitações REQUESTED.
 *
 * Uso:
 *   import { openUnpickRequestsModal } from './outputs-unpick-requests.js';
 *   openUnpickRequestsModal(companyId, outputId);
 */

import api from '../api.js';
import dialog from '../../versa/dialog.js';
import { notify } from '../../versa/msgbox.js';
import { UNPICK_REASON_LABELS, UNPICK_STATUS_LABELS } from './outputs-unpick.js';

const HOST = api.HOST;

function escapeHtml(value) {
	if (value == null) return '';
	return String(value)
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;');
}

/**
 * Formata uma data ISO para exibição amigável.
 * @param {string|null} iso
 */
function fmtDate(iso) {
	if (!iso) return '—';
	try {
		return new Date(iso).toLocaleString('pt-BR');
	} catch {
		return iso;
	}
}

/**
 * Renderiza o badge de status da solicitação.
 * @param {string} status
 */
function statusBadge(status) {
	const colors = {
		REQUESTED: '#e6a817',
		APPROVED:  '#2a7a3b',
		REJECTED:  '#c0392b',
		CANCELED:  '#888',
		EXECUTED:  '#0e7490',
	};
	const info  = UNPICK_STATUS_LABELS[status] || { label: status };
	const color = colors[status] || '#888';
	return `<span style="display:inline-block;background:${color};color:#fff;border-radius:3px;padding:1px 7px;font-size:0.78rem;font-weight:600">${escapeHtml(info.label || status)}</span>`;
}

/**
 * Modal de confirmação de aprovação ou rejeição.
 */
function openActionModal(companyId, outputId, req, action, onDone) {
	const isApprove = action === 'approve';
	const title     = isApprove ? 'Aprovar Solicitação' : 'Rejeitar Solicitação';
	const btnLabel  = isApprove ? 'Confirmar Aprovação' : 'Confirmar Rejeição';
	const noteLabel = isApprove ? 'Nota de aprovação (opcional)' : 'Motivo da rejeição (opcional)';
	const noteId    = 'unpick-action-note';
	const btnColor  = isApprove ? '#2a7a3b' : '#c0392b';

	const html = `
		<div class="msgbox" style="min-width:420px;max-width:90vw">
			<div class="mb-title">${escapeHtml(title)}</div>
			<div class="mb-text">
				<p style="margin-bottom:0.75rem;font-size:0.9rem">
					<strong>Linha:</strong> ${escapeHtml(req.output_item_number)} &nbsp;
					<strong>Qtd:</strong> ${escapeHtml(req.quantity)} &nbsp;
					<strong>Motivo:</strong> ${escapeHtml(UNPICK_REASON_LABELS[req.reason_code] || req.reason_code)}
					${req.reason_note ? `<br><span style="color:#888;font-style:italic;font-size:0.82rem">${escapeHtml(req.reason_note)}</span>` : ''}
				</p>
				<label style="display:flex;flex-direction:column;gap:4px;font-size:0.875rem">
					${escapeHtml(noteLabel)}
					<textarea id="${noteId}" rows="2"
						style="padding:4px 6px;font-size:0.875rem;resize:vertical;min-height:2.5rem"
						placeholder="Deixe em branco se não houver observação"></textarea>
				</label>
			</div>
			<button type="button" class="button mtl bn_N">Cancelar</button>
			<button type="button" class="button mtl bn_Y" style="background:${btnColor};color:#fff;border-color:${btnColor}">${escapeHtml(btnLabel)}</button>
		</div>
	`;

	const dlg = dialog.create(html, async (key) => {
		if (key === 'Escape' || key === 'N') {
			dlg.destroy();
			return;
		}
		if (key !== 'Y') return;

		const node      = dlg.getNode();
		const note      = node.querySelector(`#${noteId}`).value.trim();
		const confirmEl = node.querySelector('.bn_Y');
		confirmEl.disabled    = true;
		confirmEl.textContent = 'Aguarde…';

		const payload = isApprove
			? { request_id: req.id, approval_note: note || undefined }
			: { request_id: req.id, rejection_note: note || undefined };
		const apiFn = isApprove ? api.approve_output_unpick : api.reject_output_unpick;

		const result = await api.call(apiFn, companyId, outputId, payload);

		if (!result) {
			confirmEl.disabled    = false;
			confirmEl.textContent = btnLabel;
			return;
		}

		dlg.destroy();
		notify(
			isApprove ? 'Solicitação aprovada com sucesso.' : 'Solicitação rejeitada.',
			isApprove ? 'Unpicking' : 'Unpicking'
		);
		if (typeof onDone === 'function') onDone();
	}, /Escape|N|Y/);
}

/**
 * Abre o modal de listagem e gerenciamento de solicitações de unpicking.
 *
 * @param {string} companyId
 * @param {number} outputId
 */
export async function openUnpickRequestsModal(companyId, outputId) {

	const html = `
		<div class="msgbox" style="min-width:700px;max-width:96vw;max-height:90vh;overflow-y:auto">
			<div class="mb-title">Solicitações de Unpicking — Saída #${escapeHtml(outputId)}</div>
			<div class="mb-text" style="overflow-x:auto">
				<div id="unpick-requests-list">
					<p style="color:#888;margin:0.5rem 0">Carregando…</p>
				</div>
			</div>
			<button type="button" class="button mtl bn_N">Fechar</button>
		</div>
	`;

	const dlg = dialog.create(html, (key) => {
		if (key === 'Escape' || key === 'N') dlg.destroy();
	}, /Escape|N/);

	const node          = dlg.getNode();
	const listContainer = node.querySelector('#unpick-requests-list');

	async function renderList() {
		listContainer.innerHTML = '<p style="color:#888;margin:0.5rem 0">Carregando…</p>';

		let rows;
		try {
			const resp = await fetch(
				`${HOST}/companies/${companyId}/outputs/${outputId}/unpick-requests`,
				{ method: 'GET', credentials: 'include' }
			);
			if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
			rows = await resp.json();
		} catch (err) {
			listContainer.innerHTML = `<p style="color:#c0392b">Erro ao carregar solicitações: ${escapeHtml(err.message)}</p>`;
			return;
		}

		if (!rows || rows.length === 0) {
			listContainer.innerHTML = '<p style="color:#888;margin:0.5rem 0">Nenhuma solicitação de unpicking registrada para esta saída.</p>';
			return;
		}

		const rowsHtml = rows.map((r, idx) => {
			const canAct    = r.status === 'REQUESTED';
			const approveBtn = canAct
				? `<button class="button unpick-approve-btn" data-idx="${idx}"
					style="font-size:0.78rem;padding:2px 8px;background:#2a7a3b;color:#fff;border-color:#2a7a3b;margin-right:4px">Aprovar</button>`
				: '';
			const rejectBtn = canAct
				? `<button class="button unpick-reject-btn" data-idx="${idx}"
					style="font-size:0.78rem;padding:2px 8px;background:#c0392b;color:#fff;border-color:#c0392b">Rejeitar</button>`
				: '';

			// reason_note = observação do solicitante → exibe sob o motivo
			const reasonNote = r.reason_note
				? `<div style="color:#888;font-style:italic;font-size:0.78rem;margin-top:2px">${escapeHtml(r.reason_note)}</div>`
				: '';

			// approval_note / rejection_note → exibe sob o status
			const statusNote = (r.approval_note || r.rejection_note)
				? `<div style="color:#888;font-size:0.78rem;margin-top:2px">${escapeHtml(r.approval_note || r.rejection_note)}</div>`
				: '';

			return `
				<tr>
					<td style="padding:6px 10px;text-align:center;white-space:nowrap;vertical-align:middle">${escapeHtml(r.output_item_number)}</td>
					<td style="padding:6px 10px;text-align:right;white-space:nowrap;vertical-align:middle">${escapeHtml(r.quantity)}</td>
					<td style="padding:6px 10px;vertical-align:middle">
						${escapeHtml(UNPICK_REASON_LABELS[r.reason_code] || r.reason_code)}
						${reasonNote}
					</td>
					<td style="padding:6px 10px;vertical-align:middle;white-space:nowrap">
						${statusBadge(r.status)}
						${statusNote}
					</td>
					<td style="padding:6px 10px;color:#888;font-size:0.8rem;white-space:nowrap;vertical-align:middle">${escapeHtml(fmtDate(r.requested_at))}</td>
					<td style="padding:6px 10px;text-align:right;white-space:nowrap;vertical-align:middle">${approveBtn}${rejectBtn}</td>
				</tr>
			`;
		}).join('');

		listContainer.innerHTML = `
			<table style="width:100%;border-collapse:collapse;font-size:0.875rem">
				<thead>
					<tr style="background:#f0f0f0">
						<th style="padding:6px 10px;text-align:center;white-space:nowrap;border-bottom:1px solid #ddd">Linha</th>
						<th style="padding:6px 10px;text-align:right;white-space:nowrap;border-bottom:1px solid #ddd">Qtd</th>
						<th style="padding:6px 10px;text-align:left;border-bottom:1px solid #ddd">Motivo / Observação</th>
						<th style="padding:6px 10px;text-align:left;border-bottom:1px solid #ddd">Status</th>
						<th style="padding:6px 10px;text-align:left;white-space:nowrap;border-bottom:1px solid #ddd">Solicitado em</th>
						<th style="padding:6px 10px;border-bottom:1px solid #ddd"></th>
					</tr>
				</thead>
				<tbody>
					${rowsHtml}
				</tbody>
			</table>
		`;

		listContainer.querySelectorAll('.unpick-approve-btn').forEach(btn => {
			btn.addEventListener('click', () => {
				const r = rows[parseInt(btn.dataset.idx, 10)];
				openActionModal(companyId, outputId, r, 'approve', renderList);
			});
		});
		listContainer.querySelectorAll('.unpick-reject-btn').forEach(btn => {
			btn.addEventListener('click', () => {
				const r = rows[parseInt(btn.dataset.idx, 10)];
				openActionModal(companyId, outputId, r, 'reject', renderList);
			});
		});
	}

	renderList();
}
